// ============================================================================
// Componente: AutomacaoEmprestimo.js
// Objetivo: Interface para automação da gestão de Empréstimo Consignado (RH).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { 
    ArrowLeft, FileSpreadsheet, Play, 
    CheckCircle, AlertCircle, Loader2, Landmark, FileText, HelpCircle
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoEmprestimo = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    // Estados da Automação (Adaptado para dois tipos de inputs)
    const [arquivoFolha, setArquivoFolha] = useState(null);
    const [arquivosCsv, setArquivosCsv] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, processing, success, error
    const [executionId, setExecutionId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    // Estados de Ajuda
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    
    // Estados de Progresso
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando inicialização...');

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    // Estilos Auxiliares
    const corModulo = '#e11d48'; 

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Polling para buscar o progresso em tempo real
    useEffect(() => {
        let interval;
        if (isProcessing && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    
                    // CORREÇÃO: Utilizando a rota relativa correta que passa pelo Proxy
                    const response = await fetch(`/api/automacao-rh/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        
                        if (data.progress !== undefined) {
                            setProgress(data.progress);
                        }
                        
                        if (data.message) {
                            setStatusText(data.message);
                        } else if (data.logs && data.logs.length > 0) {
                            setStatusText(data.logs[data.logs.length - 1]);
                        }
                    }
                } catch (error) {
                    console.error("Erro ao buscar progresso:", error);
                }
            }, 1500);
        }
        return () => clearInterval(interval);
    }, [isProcessing, executionId]);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    // Handlers para o arquivo da Folha
    const handleFolhaChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setArquivoFolha(e.target.files[0]);
            resetState();
        }
    };

    // Handlers para os arquivos CSV
    const handleCsvChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setArquivosCsv(e.target.files);
            resetState();
        }
    };

    // Handlers de Drag and Drop genéricos
    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#e11d48';
        e.currentTarget.style.backgroundColor = '#fff1f2';
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
    };

    const handleDropFolha = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setArquivoFolha(e.dataTransfer.files[0]);
            resetState();
        }
    };

    const handleDropCsv = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setArquivosCsv(e.dataTransfer.files);
            resetState();
        }
    };

    const resetState = () => {
        setStatus('idle');
        setErrorMessage('');
        setProgress(0);
        setStatusText('Aguardando inicialização...');
    };

    const iniciarAutomacao = async () => {
        if (!arquivoFolha) {
            setErrorMessage('A planilha base da Folha é obrigatória.');
            return;
        }

        const newExecutionId = Date.now().toString();
        setExecutionId(newExecutionId);
        setIsProcessing(true);
        setStatus('processing');
        setProgress(5);
        setStatusText("Iniciando a comunicação com o servidor...");
        setErrorMessage('');

        const formData = new FormData();
        formData.append('arquivo_folha', arquivoFolha);
        formData.append('executionId', newExecutionId);
        
        if (arquivosCsv) {
            Array.from(arquivosCsv).forEach(file => {
                formData.append('arquivos_csv', file);
            });
        }

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            // Ajuste a rota para a correspondente no seu backend (ex: /api/automacao-rh/rh-emprestimo-consignado)
            const response = await fetch(`/api/automacao-rh/rh-emprestimo-consignado`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                setProgress(100);
                setStatusText("Processamento concluído. Iniciando download...");
                
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                
                const disposition = response.headers.get('Content-Disposition');
                let fileName = 'Relatorio_Emprestimo_Consignado.xlsx';
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                    if (matches != null && matches[1]) fileName = matches[1].replace(/['"]/g, '');
                }
                
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                
                setStatus('success');
                setStatusText("Download concluído com sucesso!");
            } else {
                const errorData = await response.json();
                setStatus('error');
                setErrorMessage(errorData.error || 'Ocorreu um erro ao processar os arquivos.');
                setStatusText(`Falha: ${errorData.error || 'Erro desconhecido'}`);
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            setStatus('error');
            setErrorMessage('Erro de conexão com o servidor. Verifique sua internet ou tente novamente.');
            setStatusText("Falha de comunicação com o servidor.");
        } finally {
            setIsProcessing(false);
        }
    };

    const getProgressBarColor = () => {
        if (status === 'error') return '#ef4444'; 
        if (status === 'success') return '#10b981'; 
        return '#e11d48'; 
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>

                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                        <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> RH
                        </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Landmark size={24} color={corModulo} /> Empréstimo Consignado
                            </h2>
                            <button
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#fff1f2', color: corModulo, border: '1px solid #fecdd3', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-5xl mx-auto">
                    
                    {/* CARTÃO PRINCIPAL DE UPLOAD */}
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Configuração da Automação</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Anexe a planilha da folha e os arquivos CSV gerados pelo sistema para realizar a conferência automática.</p>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                            
                            {/* ZONA DE UPLOAD 1: FOLHA (.XLSX) */}
                            <div 
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDropFolha}
                                style={{ 
                                    border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px 15px', 
                                    textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
                                    transition: 'all 0.2s ease', position: 'relative'
                                }}
                            >
                                <input 
                                    type="file" 
                                    accept=".xlsx, .xls" 
                                    onChange={handleFolhaChange}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    disabled={isProcessing}
                                />
                                
                                {!arquivoFolha ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ backgroundColor: '#ffe4e6', padding: '12px', borderRadius: '50%', color: '#e11d48' }}>
                                            <FileSpreadsheet size={24} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#334155', fontSize: '14px' }}>Planilha da Folha</p>
                                            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Apenas arquivo Excel (.xlsx)</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ backgroundColor: '#ecfdf5', padding: '12px', borderRadius: '50%', color: '#10b981' }}>
                                            <CheckCircle size={24} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#0f172a', fontSize: '14px', wordBreak: 'break-all' }}>{arquivoFolha.name}</p>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{(arquivoFolha.size / 1024 / 1024).toFixed(2)} MB</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ZONA DE UPLOAD 2: CSVS (MÚLTIPLOS) */}
                            <div 
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDropCsv}
                                style={{ 
                                    border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px 15px', 
                                    textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
                                    transition: 'all 0.2s ease', position: 'relative'
                                }}
                            >
                                <input 
                                    type="file" 
                                    accept=".csv" 
                                    multiple
                                    onChange={handleCsvChange}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    disabled={isProcessing}
                                />
                                
                                {!arquivosCsv || arquivosCsv.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ backgroundColor: '#ffe4e6', padding: '12px', borderRadius: '50%', color: '#e11d48' }}>
                                            <FileText size={24} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#334155', fontSize: '14px' }}>Arquivos Empréstimo</p>
                                            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Múltiplos arquivos (.csv)</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ backgroundColor: '#ecfdf5', padding: '12px', borderRadius: '50%', color: '#10b981' }}>
                                            <CheckCircle size={24} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#0f172a', fontSize: '14px' }}>
                                                {arquivosCsv.length} arquivo(s) selecionado(s)
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {errorMessage && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '14px', fontWeight: '500' }}>
                                <AlertCircle size={18} /> {errorMessage}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={iniciarAutomacao}
                                disabled={isProcessing || !arquivoFolha}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', 
                                    backgroundColor: isProcessing || !arquivoFolha ? '#cbd5e1' : '#e11d48', 
                                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', 
                                    fontSize: '15px', cursor: isProcessing || !arquivoFolha ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.2)'
                                }}
                            >
                                {isProcessing ? (
                                    <><Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} /> Processando...</>
                                ) : (
                                    <><Play size={18} /> Iniciar Processamento</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* CARD DE PROGRESSO */}
                    {status !== 'idle' && (
                        <div style={{ 
                            backgroundColor: '#fff', 
                            borderRadius: '16px', 
                            padding: '24px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {status === 'processing' && <Loader2 size={24} color="#0ea5e9" className="animate-spin" />}
                                    {status === 'success' && <CheckCircle size={24} color="#10b981" />}
                                    {status === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ 
                                width: '100%', height: '12px', backgroundColor: '#f1f5f9', 
                                borderRadius: '99px', overflow: 'hidden'
                            }}>
                                <div style={{ 
                                    width: `${progress}%`, height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>
            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA VALE TRANSPORTE */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="emprestimo" 
                showOnlyIds={['emprestimo']} 
            />
        </div>
    );
};

export default AutomacaoEmprestimo;